import { getIncludedCoreSeatsQuantity, getProductLevelForCoreSeats, getSortedProductConfigurations, getSuiteProductConfigurations } from 'pricing-pages-lib/utils/planBuilderUtils';
import { getBundledProductLinesForSkuId } from 'pricing-pages-lib/utils/productsUtils';
import { createSelectorFromUtil } from 'pricing-pages-lib/utils/selectorUtils';
import { ANNUAL, MONTHLY } from 'self-service-api/constants/Terms';
import { checkoutProductConfigurationsSelector, convertSkuIdsToSkuUuidsForProductConfigurationsSelector } from './ProductConfigsSelectors';
import { skuIdIsQuantityPackSelector } from './ProductsSelectors';
import { transformState } from './SelectorUtils';
export const planBuilderIsLoadingSelector = state => state.planBuilder.isLoading;
export const planBuilderHasErrorSelector = state => state.planBuilder.hasError;
export const planBuilderProductConfigurationsSelector = state => state.planBuilder.productConfigurations;
export const planBuilderCountryCodeSelector = state => state.planBuilder.countryCode;
export const planBuilderPurchaseOptionSelector = state => state.planBuilder.purchaseOption;
export const planBuilderQuantityPackPurchaseOptionsSelector = state => state.planBuilder.quantityPackPurchaseOptions;
export const planBuilderHasCommonTermsSelector = state => state.planBuilder.hasCommonTerms;

/**
 * Ensures required onboarding is added to productConfigurations before checkout.
 * If onboarding is optional, it will be added automatically.
 */
export const planBuilderCheckoutProductConfigurationsSelector = state => {
  const productConfigurations = planBuilderProductConfigurationsSelector(state);
  const checkoutConfigs = checkoutProductConfigurationsSelector(state, {
    productConfigurations
  });
  if (checkoutConfigs.length === 0) return [];
  return checkoutConfigs;
};

/**
 * For the price breakdown modal, display all checkout configurations.
 * Include onboarding only if required.
 */
export const planBuilderBreakdownDisplayProductConfigurationsSelector = state => {
  const productConfigurations = planBuilderProductConfigurationsSelector(state);
  return checkoutProductConfigurationsSelector(state, {
    productConfigurations,
    includeOnboarding: true,
    includeOnboardingFallback: false
  });
};
const pricesForQuantityPackQuantitySelector = (state, {
  productConfiguration
}) => {
  const isQuantityPack = skuIdIsQuantityPackSelector(state, {
    skuId: productConfiguration.skuId
  });
  const skuId = productConfiguration.skuId;
  if (!isQuantityPack) return undefined;
  const quantityPackPurchaseOptions = planBuilderQuantityPackPurchaseOptionsSelector(state);
  const pricesForQuantityPackQuantity = quantityPackPurchaseOptions.find(option => option.skuId === skuId);
  return pricesForQuantityPackQuantity;
};
const totalPricesForQuantityPacksSelector = state => {
  const quantityPackPurchaseOptions = planBuilderQuantityPackPurchaseOptionsSelector(state);
  const productConfigurations = planBuilderProductConfigurationsSelector(state);
  const initialPrice = {
    priceForCommittedPeriod: 0,
    monthlyPrice: 0
  };
  const totalDiscountedPrice = Object.assign({}, initialPrice);
  const totalListPrice = Object.assign({}, initialPrice);
  quantityPackPurchaseOptions.forEach(({
    skuId
  }) => {
    const quantityPackConfig = productConfigurations.find(config => config.skuId === skuId);
    if (!quantityPackConfig) return;
    const pricesForQuantityPack = pricesForQuantityPackQuantitySelector(state, {
      productConfiguration: quantityPackConfig
    });
    const pricesForQuantityPackQuantity = pricesForQuantityPack === null || pricesForQuantityPack === void 0 ? void 0 : pricesForQuantityPack.quantityPackPrice;
    if (pricesForQuantityPackQuantity) {
      // totalDiscountedPrice
      totalDiscountedPrice.priceForCommittedPeriod += Number(pricesForQuantityPackQuantity.discountedPrice.priceForCommittedPeriod);
      totalDiscountedPrice.monthlyPrice += Number(pricesForQuantityPackQuantity.discountedPrice.monthlyPrice);
      // totalListPrice
      totalListPrice.priceForCommittedPeriod += Number(pricesForQuantityPackQuantity.listPrice.priceForCommittedPeriod);
      totalListPrice.monthlyPrice += Number(pricesForQuantityPackQuantity.listPrice.monthlyPrice);
    }
  });
  return {
    totalDiscountedPrice,
    totalListPrice
  };
};
export const planBuilderMonthlyListPriceForQuantityPackSelector = (state, {
  productConfiguration
}) => {
  var _pricesForQuantityPac;
  const pricesForQuantityPackQuantity = pricesForQuantityPackQuantitySelector(state, {
    productConfiguration
  });
  const monthlyListPrice = pricesForQuantityPackQuantity === null || pricesForQuantityPackQuantity === void 0 || (_pricesForQuantityPac = pricesForQuantityPackQuantity.quantityPackPrice) === null || _pricesForQuantityPac === void 0 || (_pricesForQuantityPac = _pricesForQuantityPac.listPrice) === null || _pricesForQuantityPac === void 0 ? void 0 : _pricesForQuantityPac.monthlyPrice;
  return monthlyListPrice ? Number(monthlyListPrice) : undefined;
};
export const planBuilderMonthlyDiscountedPriceForQuantityPackSelector = (state, {
  productConfiguration
}) => {
  var _pricesForQuantityPac2;
  const hasCommonTerms = planBuilderHasCommonTermsSelector(state);
  if (!hasCommonTerms) {
    return planBuilderMonthlyListPriceForQuantityPackSelector(state, {
      productConfiguration
    });
  }
  const pricesForQuantityPackQuantity = pricesForQuantityPackQuantitySelector(state, {
    productConfiguration
  });
  const monthlyDiscountedPrice = pricesForQuantityPackQuantity === null || pricesForQuantityPackQuantity === void 0 || (_pricesForQuantityPac2 = pricesForQuantityPackQuantity.quantityPackPrice) === null || _pricesForQuantityPac2 === void 0 || (_pricesForQuantityPac2 = _pricesForQuantityPac2.discountedPrice) === null || _pricesForQuantityPac2 === void 0 ? void 0 : _pricesForQuantityPac2.monthlyPrice;
  return monthlyDiscountedPrice ? Number(monthlyDiscountedPrice) : undefined;
};
const purchaseOptionForProductConfiguration = (state, {
  productConfiguration
}) => {
  var _selectedProduct$, _planBuilderPurchaseO;
  const isQuantityPack = skuIdIsQuantityPackSelector(state, {
    skuId: productConfiguration.skuId
  });
  if (isQuantityPack) return undefined;
  const planBuilderPurchaseOption = planBuilderPurchaseOptionSelector(state);
  const selectedProduct = planBuilderPurchaseOption ? convertSkuIdsToSkuUuidsForProductConfigurationsSelector(state, {
    productConfigurations: [productConfiguration]
  }) : undefined;
  const selectedSkuUuid = selectedProduct === null || selectedProduct === void 0 || (_selectedProduct$ = selectedProduct[0]) === null || _selectedProduct$ === void 0 ? void 0 : _selectedProduct$.skuUuid;
  const purchaseOption = selectedSkuUuid ? planBuilderPurchaseOption === null || planBuilderPurchaseOption === void 0 || (_planBuilderPurchaseO = planBuilderPurchaseOption.perProductPricing) === null || _planBuilderPurchaseO === void 0 ? void 0 : _planBuilderPurchaseO[selectedSkuUuid] : undefined;
  return purchaseOption;
};
export const planBuilderDiscountForProductSelector = (state, {
  productConfiguration
}) => {
  const hasCommonTerms = planBuilderHasCommonTermsSelector(state);
  // If there are no common terms between selected products, we're showing list prices
  // which means there's no visible discount.
  if (!hasCommonTerms) {
    return 0;
  }
  const purchaseOption = purchaseOptionForProductConfiguration(state, {
    productConfiguration
  });
  if (!purchaseOption) return undefined;
  const getPriceForCommittedPeriod = priceObj => {
    if ('priceForCommittedPeriod' in priceObj) {
      return Number(priceObj.priceForCommittedPeriod);
    }
    return Number(priceObj.price);
  };
  const discountedPrice = getPriceForCommittedPeriod(purchaseOption.discountedPrice);
  const listPrice = getPriceForCommittedPeriod(purchaseOption.listPrice);
  if (!discountedPrice || !listPrice) return undefined;
  const discountAmount = 100 - 100 * discountedPrice / listPrice;
  return discountAmount;
};
export const planBuilderMonthlyListPriceForProductSelector = (state, {
  productConfiguration
}) => {
  const purchaseOption = purchaseOptionForProductConfiguration(state, {
    productConfiguration
  });
  const listPrice = purchaseOption === null || purchaseOption === void 0 ? void 0 : purchaseOption.listPrice;
  const monthlyListPrice = listPrice ? 'monthlyPrice' in listPrice ? listPrice.monthlyPrice : listPrice.price : undefined;
  return monthlyListPrice ? Number(monthlyListPrice) : undefined;
};
export const planBuilderMonthlyDiscountedPriceForProductSelector = (state, {
  productConfiguration
}) => {
  const hasCommonTerms = planBuilderHasCommonTermsSelector(state);
  if (!hasCommonTerms) {
    return planBuilderMonthlyListPriceForProductSelector(state, {
      productConfiguration
    });
  }
  const purchaseOption = purchaseOptionForProductConfiguration(state, {
    productConfiguration
  });
  const discountedPrice = purchaseOption === null || purchaseOption === void 0 ? void 0 : purchaseOption.discountedPrice;
  const monthlyDiscountedPrice = discountedPrice ? 'monthlyPrice' in discountedPrice ? discountedPrice.monthlyPrice : discountedPrice.price : undefined;
  return monthlyDiscountedPrice ? Number(monthlyDiscountedPrice) : undefined;
};
export const planBuilderPaymentTermForProductsSelector = state => {
  const purchaseOptions = planBuilderPurchaseOptionSelector(state);
  if (!purchaseOptions) return undefined;
  const paymentTermNum = purchaseOptions.paymentTerm;
  return paymentTermNum >= 12 ? ANNUAL : MONTHLY;
};
export const planBuilderTotalMonthlyRecurringListPriceCostSelector = state => {
  const purchaseOption = planBuilderPurchaseOptionSelector(state);
  if (!purchaseOption) return undefined;

  // PRODUCTS
  const recurringMonthlyProductPrice = purchaseOption.monthly.fullPrice;

  // QUANTITY PACKS
  const quantityPackTotalPrices = totalPricesForQuantityPacksSelector(state);
  const recurringMonthlyQuantityPackPrice = quantityPackTotalPrices.totalListPrice.monthlyPrice;
  return recurringMonthlyProductPrice + recurringMonthlyQuantityPackPrice;
};
export const planBuilderTotalMonthlyRecurringCalculatedPriceCostSelector = state => {
  const hasCommonTerms = planBuilderHasCommonTermsSelector(state);
  if (!hasCommonTerms) {
    return planBuilderTotalMonthlyRecurringListPriceCostSelector(state);
  }
  const purchaseOption = planBuilderPurchaseOptionSelector(state);
  if (!purchaseOption) return undefined;

  // PRODUCTS
  const recurringMonthlyProductPrice = purchaseOption.monthly.calculatedPrice;

  // QUANTITY PACKS
  const quantityPackTotalPrices = totalPricesForQuantityPacksSelector(state);
  const recurringMonthlyQuantityPackPrice = quantityPackTotalPrices.totalDiscountedPrice.monthlyPrice;
  return recurringMonthlyProductPrice + recurringMonthlyQuantityPackPrice;
};
export const planBuilderTotalAnnualRecurringListPriceCostSelector = state => {
  const purchaseOption = planBuilderPurchaseOptionSelector(state);
  if (!purchaseOption) return undefined;

  // PRODUCTS
  const recurringAnnualProductPrice = purchaseOption.annually.fullPrice;

  // QUANTITY PACKS
  const quantityPackTotalPrices = totalPricesForQuantityPacksSelector(state);
  const recurringAnnualQuantityPackPrice = quantityPackTotalPrices.totalListPrice.priceForCommittedPeriod;
  return recurringAnnualProductPrice + recurringAnnualQuantityPackPrice;
};
export const planBuilderTotalAnnualRecurringCalculatedPriceCostSelector = state => {
  const hasCommonTerms = planBuilderHasCommonTermsSelector(state);
  if (!hasCommonTerms) {
    return planBuilderTotalAnnualRecurringListPriceCostSelector(state);
  }
  const purchaseOption = planBuilderPurchaseOptionSelector(state);
  if (!purchaseOption) return undefined;

  // PRODUCTS
  const recurringAnnualProductPrice = purchaseOption.annually.calculatedPrice;

  // QUANTITY PACKS
  const quantityPackTotalPrices = totalPricesForQuantityPacksSelector(state);
  const recurringAnnualQuantityPackPrice = quantityPackTotalPrices.totalDiscountedPrice.priceForCommittedPeriod;
  return recurringAnnualProductPrice + recurringAnnualQuantityPackPrice;
};
export const planBuilderTotalFullPriceOneTimeFeesSelector = state => {
  var _purchaseOption$oneTi;
  const purchaseOption = planBuilderPurchaseOptionSelector(state);
  if (!purchaseOption) return undefined;
  const oneTimeFeePrice = (_purchaseOption$oneTi = purchaseOption.oneTimeFee) === null || _purchaseOption$oneTi === void 0 ? void 0 : _purchaseOption$oneTi.fullPrice;
  return oneTimeFeePrice;
};
export const planBuilderTotalDiscountedOneTimeFeesSelector = state => {
  var _purchaseOption$oneTi2;
  const hasCommonTerms = planBuilderHasCommonTermsSelector(state);
  if (!hasCommonTerms) {
    return planBuilderTotalFullPriceOneTimeFeesSelector(state);
  }
  const purchaseOption = planBuilderPurchaseOptionSelector(state);
  if (!purchaseOption) return undefined;
  const oneTimeFeePrice = (_purchaseOption$oneTi2 = purchaseOption.oneTimeFee) === null || _purchaseOption$oneTi2 === void 0 ? void 0 : _purchaseOption$oneTi2.calculatedPrice;
  return oneTimeFeePrice;
};
export const planBuilderCostToGetStartedListPricesSelector = state => {
  const purchaseOption = planBuilderPurchaseOptionSelector(state);
  if (!purchaseOption) return undefined;
  const paymentTerm = purchaseOption.paymentTerm;
  const oneTimeFeesPrice = planBuilderTotalFullPriceOneTimeFeesSelector(state);
  const recurringTotalPrice = paymentTerm === 12 ? planBuilderTotalAnnualRecurringListPriceCostSelector(state) : planBuilderTotalMonthlyRecurringListPriceCostSelector(state);
  return oneTimeFeesPrice === undefined || recurringTotalPrice === undefined ? undefined : oneTimeFeesPrice + recurringTotalPrice;
};
export const planBuilderCostToGetStartedSelector = state => {
  const hasCommonTerms = planBuilderHasCommonTermsSelector(state);
  if (!hasCommonTerms) {
    return planBuilderCostToGetStartedListPricesSelector(state);
  }
  const purchaseOption = planBuilderPurchaseOptionSelector(state);
  if (!purchaseOption) return undefined;
  const paymentTerm = purchaseOption.paymentTerm;
  const oneTimeFeesPrice = planBuilderTotalDiscountedOneTimeFeesSelector(state);
  const recurringTotalPrice = paymentTerm === 12 ? planBuilderTotalAnnualRecurringCalculatedPriceCostSelector(state) : planBuilderTotalMonthlyRecurringCalculatedPriceCostSelector(state);
  return oneTimeFeesPrice === undefined || recurringTotalPrice === undefined ? undefined : oneTimeFeesPrice + recurringTotalPrice;
};
export const planBuilderPurchaseOptionPaymentTermSelector = state => {
  const purchaseOption = planBuilderPurchaseOptionSelector(state);
  if (!purchaseOption) return undefined;
  if (purchaseOption.paymentTerm >= 12) return 'annual';
  return 'monthly';
};
export const planBuilderEligibleAddonsSelector = state => {
  return state.planBuilder.eligibleAddons;
};
export const suiteProductConfigurationsSelector = createSelectorFromUtil(getSuiteProductConfigurations, transformState);
export const bundledProductLinesForSkuIdSelector = createSelectorFromUtil(getBundledProductLinesForSkuId, transformState);
export const productLevelForCoreSeatsSelector = createSelectorFromUtil(getProductLevelForCoreSeats, transformState);
export const includedCoreSeatsQuantitySelector = createSelectorFromUtil(getIncludedCoreSeatsQuantity, transformState);
export const sortedProductConfigurationsSelector = createSelectorFromUtil(getSortedProductConfigurations, transformState);